import { useEffect, useState } from 'react'
import { Ban, Loader2, Plus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api, getApiErrorMessage } from '@/lib/api'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'

interface BanRecord {
  id: number
  account_id: string | null
  email_hash: string
  reason: string
  ban_type: string
  expires_at: string | null
  banned_by: string
  evidence: Record<string, any>
  created_at: string
}

const BAN_REASONS = ['spam_campaign', 'phishing', 'malware', 'repeated_abuse', 'confirmed_spam']

// CHECKPOINT: PRD-09 FR-9.7 — Ban list and irreversible ban creation
export function BansTab() {
  const [bans, setBans] = useState<BanRecord[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [reason, setReason] = useState('')
  const [banType, setBanType] = useState('permanent')
  const LIMIT = 50

  const fetchBans = async (newOffset = offset) => {
    setLoading(true)
    try {
      const res = await api.get('/admin/security/bans', {
        params: { page: Math.floor(newOffset / LIMIT) + 1, per_page: LIMIT },
      })
      setBans(res.data.bans || [])
      setTotal(res.data.total || 0)
      setOffset(newOffset)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load bans'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBans(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBan = async () => {
    if (!accountId || !reason) {
      toast.error('Account ID and reason are required')
      return
    }
    setSubmitting(true)
    try {
      await api.post('/admin/security/bans', {
        account_id: accountId,
        reason,
        ban_type: banType,
        evidence: {},
      })
      toast.success('Account banned')
      setDialogOpen(false)
      setAccountId('')
      setReason('')
      fetchBans(0)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to ban account'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Ban Account
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Ban className="h-4 w-4" />
            Ban List
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Account</th>
                  <th className="px-4 py-3 text-left font-medium">Reason</th>
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Email Hash</th>
                  <th className="px-4 py-3 text-left font-medium">Banned By</th>
                  <th className="px-4 py-3 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : bans.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No bans on record
                    </td>
                  </tr>
                ) : (
                  bans.map((b) => (
                    <tr key={b.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-muted-foreground">
                        {b.account_id ? b.account_id.slice(0, 8) + '...' : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium">{b.reason}</td>
                      <td className="px-4 py-3">
                        <span className={b.ban_type === 'permanent' ? 'text-red-500' : 'text-amber-500'}>
                          {b.ban_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs truncate max-w-xs">{b.email_hash}</td>
                      <td className="px-4 py-3 text-muted-foreground">{b.banned_by.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(b.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={fetchBans} />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Account ID</label>
              <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="uuid" />
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="">Select reason</option>
                {BAN_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Ban Type</label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={banType}
                onChange={(e) => setBanType(e.target.value)}
              >
                <option value="permanent">Permanent</option>
                <option value="temporary">Temporary</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBan} disabled={submitting} variant="destructive">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Ban Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
